import { env } from '@/lib/env';
import { getSite } from '@/lib/site';
import { isNative } from '@/lib/platform/platform';
import { supabase } from '@/lib/supabase/client';
import type { AppRole, Profile } from '@/types/domain';

export const PASSWORD_MIN_LENGTH = 8;

export function validateEmail(email: string): string | null {
  if (!email.trim()) return 'Enter your email address.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email address.';
  return null;
}

export function validatePassword(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) return `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) return 'Use a mix of letters and numbers.';
  return null;
}

/** Where auth emails send users back to (web URL or native deep link). */
/** Only same-app paths, never another site ("//evil.example"). */
export function safeNext(next: string | null | undefined): string | null {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

function redirectTo(path: string) {
  const base = isNative() ? env.appUrl : window.location.origin;
  return `${base}${path}`;
}

export const authService = {
  async signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) throw error;
  },

  /** `next`: an in-app path to open after confirming the email (e.g. a trial page). */
  async signUp(email: string, password: string, displayName: string, next?: string | null, acquisition?: Record<string, string>) {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { display_name: displayName.trim(), site: getSite().code, ...(acquisition ? { acquisition } : {}) }, emailRedirectTo: redirectTo(safeNext(next) ?? '/dashboard') },
    });
    if (error) throw error;
    // When email confirmation is enabled there is no session yet.
    return { needsConfirmation: !data.session };
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async requestPasswordReset(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: redirectTo('/reset-password') });
    if (error) throw error;
  },

  async updatePassword(password: string) {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  },

  async fetchRoles(): Promise<AppRole[]> {
    const { data, error } = await supabase.rpc('my_roles');
    if (error) throw error;
    return (data as AppRole[]) ?? [];
  },

  async fetchProfile(userId: string): Promise<Profile | null> {
    const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
    if (error) throw error;
    return data as Profile | null;
  },
};
